import { Photo } from '@watchduty/camera';
import { useHistory } from 'react-router-dom';
import { useCapacitorListener } from 'hooks/useCapacitorListener';
import {
  attachPhotoToDraft,
  getPhotoSubmissionDraft,
  isPhotoDraftRecoverySupported,
} from 'features/reports/utils/photoSubmissionDraft';

/**
 * Recovers a photo capture that outlived our process. On Android, `Camera.getPhoto()` launches
 * an external Activity, and the OS may kill our process while it has focus; Capacitor persists
 * the pending call natively and replays the result via `appRestoredResult` on relaunch. Without
 * this listener that result is silently dropped.
 *
 * Only one listener may exist app-wide: Capacitor holds the result until the first
 * listener registers, then delivers it exactly once.
 */
export const RestoredPhotoListener = (): null => {
  const myHistory = useHistory();

  useCapacitorListener(
    'appRestoredResult',
    (event) => {
      if (event.pluginId !== 'Camera' || event.methodName !== 'getPhoto') {
        return;
      }

      // A successful result attaches the recovered photo. An unsuccessful one (the user
      // cancelled the camera, or it failed) still restores the route and typed message, since
      // those were saved before the camera ever launched.
      const draft =
        event.success && event.data
          ? attachPhotoToDraft(event.data as Photo)
          : getPhotoSubmissionDraft();

      if (!draft) return;

      // `replace()`, not `push()`: prevent the back button from landing back on '/'
      myHistory.replace(draft.route);
    },
    { enabled: isPhotoDraftRecoverySupported() },
  );

  return null;
};
